import {
  Currency,
  Provider,
  FeeBreakdown,
  ProviderInfo,
  RatesData,
} from '../types/calculator'

export const PROVIDERS: Record<Provider, ProviderInfo> = {
  djamo: {
    id: 'djamo',
    name: 'Djamo CI',
    country: 'CI',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    logoUrl:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/pU4XWPGE1mVv5heDhG1qxi/pasted-image.png',
    appUrl: {
      ios: 'https://apps.apple.com/us/app/djamo/id1504072402',
      android: 'https://play.google.com/store/apps/details?id=com.djamo.app',
    },
  },
  djamosn: {
    id: 'djamosn',
    name: 'Djamo SN',
    country: 'SN',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    logoUrl:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/pU4XWPGE1mVv5heDhG1qxi/pasted-image.png',
    appUrl: {
      ios: 'https://apps.apple.com/us/app/djamo/id1504072402',
      android: 'https://play.google.com/store/apps/details?id=com.djamo.app',
    },
  },
  wave: {
    id: 'wave',
    name: 'Wave',
    country: 'ALL',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    logoUrl:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/wXFLnwrKZVoAsEt26LhbEE/pasted-image.png',
    appUrl: {
      ios: 'https://apps.apple.com/sn/app/wave-mobile-money/id1523884528',
      android:
        'https://play.google.com/store/apps/details?id=com.wave.personal',
    },
    deepLinkScheme: 'wave://',
  },
  pushci: {
    id: 'pushci',
    name: 'PushCI',
    country: 'CI',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    logoUrl:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/34NWVjA9deJarYy3du8QqH/pasted-image.png',
    appUrl: {
      ios: 'https://apps.apple.com/ng/app/push-ci/id1601308812',
      android:
        'https://play.google.com/store/apps/details?id=com.panelyscash.app',
    },
  },
  orange: {
    id: 'orange',
    name: 'Orange Money Senegal',
    country: 'SN',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    logoUrl:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/o3iMEi8oVSwSkrdC4JwDJD/pasted-image.png',
    appUrl: {
      ios: 'https://apps.apple.com/sn/app/orange-max-it-s%C3%A9n%C3%A9gal/id1039327980',
      android:
        'https://play.google.com/store/apps/details?id=com.orange.myorange.osn',
    },
  },
  nafolo: {
    id: 'nafolo',
    name: 'Nafolo CI',
    country: 'CI',
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    logoUrl:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/hKxjoETLPFbbi9fTakjcGM/pasted-image.png',
    appUrl: {
      ios: 'https://apps.apple.com/ci/app/nafolo/id6479621423',
      android:
        'https://play.google.com/store/apps/details?id=com.xearth.nafolo',
    },
  },
}

// Default provider fees (fallback if Supabase not configured)
const DEFAULT_PROVIDER_FEES: Record<Provider, number | 'not_defined'> = {
  djamo: 200,
  djamosn: 200,
  wave: 0,
  pushci: 290,
  orange: 'not_defined',
  nafolo: 0,
}

// Default FX rates used for calculation (fallback if Supabase not configured)
const DEFAULT_FX_RATES_CALCULATION: Record<
  Provider,
  Record<Currency, number>
> = {
  djamo: {
    XOF: 1,
    EUR: 682,
    USD: 603,
  },
  djamosn: {
    XOF: 1,
    EUR: 676,
    USD: 601,
  },
  wave: {
    XOF: 1,
    EUR: 675.68,
    USD: 594.3,
  },
  pushci: {
    XOF: 1,
    EUR: 685,
    USD: 611,
  },
  orange: {
    XOF: 1,
    EUR: 669.754,
    USD: 585.773,
  },
  nafolo: {
    XOF: 1,
    EUR: 693,
    USD: 605,
  },
}

// Default displayed FX rates shown in UI (fallback if Supabase not configured)
const DEFAULT_FX_RATES_DISPLAY: Record<Provider, Record<Currency, number>> = {
  djamo: {
    XOF: 1,
    EUR: 682,
    USD: 603,
  },
  djamosn: {
    XOF: 1,
    EUR: 676,
    USD: 601,
  },
  wave: {
    XOF: 1,
    EUR: 676,
    USD: 594,
  },
  pushci: {
    XOF: 1,
    EUR: 685,
    USD: 611,
  },
  orange: {
    XOF: 1,
    EUR: 669.754,
    USD: 585.773,
  },
  nafolo: {
    XOF: 1,
    EUR: 693,
    USD: 605,
  },
}

// Dynamic rates storage (will be updated from Supabase)
let currentRatesData: RatesData | null = null

/**
 * Update the current rates data (called when fetching from Supabase)
 */
export function updateRatesData(data: RatesData | null) {
  currentRatesData = data
}

/**
 * Get current provider fees (from Supabase or fallback to defaults)
 */
export function getProviderFees(): Record<Provider, number | 'not_defined'> {
  return currentRatesData?.fees || DEFAULT_PROVIDER_FEES
}

/**
 * Get calculation rates (from Supabase or fallback to defaults)
 */
function getCalculationRates(): Record<Provider, Record<Currency, number>> {
  if (!currentRatesData) return DEFAULT_FX_RATES_CALCULATION

  const rates: Record<string, Record<string, number>> = {}
  Object.entries(currentRatesData.rates).forEach(([provider, currencies]) => {
    rates[provider] = {}
    Object.entries(currencies).forEach(([currency, rateData]) => {
      rates[provider][currency] = rateData.calculation
    })
  })
  return rates as Record<Provider, Record<Currency, number>>
}

/**
 * Get display rates (from Supabase or fallback to defaults)
 */
function getDisplayRates(): Record<Provider, Record<Currency, number>> {
  if (!currentRatesData) return DEFAULT_FX_RATES_DISPLAY

  const rates: Record<string, Record<string, number>> = {}
  Object.entries(currentRatesData.rates).forEach(([provider, currencies]) => {
    rates[provider] = {}
    Object.entries(currencies).forEach(([currency, rateData]) => {
      rates[provider][currency] = rateData.display
    })
  })
  return rates as Record<Provider, Record<Currency, number>>
}

export function calculateFees(
  amount: number,
  currency: Currency,
  provider: Provider,
): FeeBreakdown {
  const calculationRates = getCalculationRates()
  const displayRates = getDisplayRates()

  const fxRate = calculationRates[provider][currency]
  const displayedFxRate = displayRates[provider][currency]
  const totalCost = currency === 'XOF' ? amount : amount * fxRate

  return {
    fixedFee: 0,
    percentageFee: 0,
    fxRate,
    displayedFxRate,
    totalCost: Math.round(totalCost),
  }
}

export function formatXOF(amount: number): string {
  return (
    new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount) + ' XOF'
  )
}
